import { z } from 'zod';

/**
 * Esquemas das entidades (plano, seção 16).
 *
 * O formato é o mesmo no servidor (JSON da API), na sincronização e no banco local.
 * Por isso os campos usam snake_case, como o Laravel devolve, e nomes em inglês
 * (convenção da seção 21.2). Dinheiro sempre em centavos (inteiro).
 */

export const isoDateTime = z.iso.datetime({ offset: true });
const isoNullable = isoDateTime.nullable();

export const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
export const localTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, 'Hora no formato HH:MM');

export const ENTITY_TYPES = [
  'professional',
  'service',
  'client',
  'availability_rule',
  'availability_window',
  'block',
  'appointment',
  'payment',
  'package',
  'message_template',
  'product',
  'stock_movement',
  'sale',
  'service_supply',
  'appointment_service',
] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];
export const entityTypeSchema = z.enum(ENTITY_TYPES);

/** Campos comuns a toda entidade sincronizável (seção 16.2). */
export const syncFields = {
  id: z.uuid(),
  created_at: isoNullable.default(null),
  /** hora do servidor ao gravar */
  updated_at: isoNullable.default(null),
  /** hora do aparelho na última edição */
  client_updated_at: isoNullable.default(null),
  /** incrementado pelo servidor; 0 = nunca sincronizado */
  version: z.number().int().nonnegative().default(0),
  /** exclusão lógica */
  deleted_at: isoNullable.default(null),
};

const owned = {
  ...syncFields,
  professional_id: z.uuid(),
};

// ---------------------------------------------------------------- professional

export const SCHEDULE_MODES = ['fixed', 'weekly'] as const;
export const APPROVAL_MODES = ['auto', 'manual'] as const;
export const PLANS = ['essencial', 'estetica'] as const;
export const SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'expired', 'canceled'] as const;

export const professionalSchema = z.object({
  ...syncFields,
  name: z.string().trim().min(1).max(120),
  business_name: z.string().trim().max(120).nullable().default(null),
  slug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{1,46}[a-z0-9])$/).nullable().default(null),
  email: z.email().nullable().default(null),
  /** WhatsApp da profissional, só dígitos com DDI */
  phone: z.string().regex(/^\d{12,13}$/).nullable().default(null),
  timezone: z.string().min(3).default('America/Sao_Paulo'),
  city: z.string().max(80).nullable().default(null),
  address: z.string().max(240).nullable().default(null),
  serves_at_studio: z.boolean().default(true),
  serves_at_home: z.boolean().default(false),
  schedule_mode: z.enum(SCHEDULE_MODES).default('fixed'),
  approval_mode: z.enum(APPROVAL_MODES).default('auto'),
  /** antecedência mínima para o cliente marcar */
  min_notice_min: z.number().int().min(0).max(10_080).default(120),
  /** quantos dias à frente o link mostra */
  max_days_ahead: z.number().int().min(1).max(180).default(30),
  /** até quantos minutos antes o cliente pode remarcar ou cancelar sozinho */
  cancel_deadline_min: z.number().int().min(0).max(10_080).default(240),
  /** intervalo entre atendimentos (higienizar, preparar) */
  buffer_min: z.number().int().min(0).max(240).default(10),
  /** passo dos horários oferecidos no link */
  slot_step_min: z.number().int().min(5).max(120).default(15),
  /** deslocamento fixo para atendimento em domicílio */
  travel_min: z.number().int().min(0).max(240).default(30),
  /** prazo da lista Quem sumiu */
  missing_after_days: z.number().int().min(7).max(365).default(21),
  /** limite de agendamentos futuros por WhatsApp pelo link */
  max_future_bookings: z.number().int().min(1).max(20).default(2),
  welcome_text: z.string().max(500).nullable().default(null),
  rules_text: z.string().max(500).nullable().default(null),
  theme_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#7A1F45'),
  /** hora local do aviso "Amanhã: N atendimentos" */
  reminder_hour: z.number().int().min(0).max(23).default(19),
  /** modo semanal: lembrar de abrir a próxima semana */
  open_week_reminder: z.boolean().default(true),
  open_week_reminder_weekday: z.number().int().min(0).max(6).default(0),
  open_week_reminder_hour: z.number().int().min(0).max(23).default(20),
  plan: z.enum(PLANS).default('essencial'),
  subscription_status: z.enum(SUBSCRIPTION_STATUSES).default('trialing'),
  trial_ends_at: isoNullable.default(null),
  current_period_end: isoNullable.default(null),
  onboarded_at: isoNullable.default(null),
});
export type Professional = z.infer<typeof professionalSchema>;

/** Campos que só o servidor altera (o aparelho não sobrescreve). */
export const PROFESSIONAL_READONLY_FIELDS = [
  'email',
  'slug',
  'plan',
  'subscription_status',
  'trial_ends_at',
  'current_period_end',
] as const;

// ---------------------------------------------------------------- service

export const serviceSchema = z.object({
  ...owned,
  name: z.string().trim().min(1).max(80),
  duration_min: z.number().int().min(5).max(720),
  price_cents: z.number().int().min(0).max(10_000_000),
  visible_on_link: z.boolean().default(true),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().default(null),
  sort_order: z.number().int().default(0),
  active: z.boolean().default(true),
  /** dias até a próxima visita sugerida (ex.: manutenção de gel = 21) */
  return_days: z.number().int().min(1).max(365).nullable().default(null),
});
export type Service = z.infer<typeof serviceSchema>;

// ---------------------------------------------------------------- client

export const clientSchema = z.object({
  ...owned,
  name: z.string().trim().min(1).max(120),
  /** só dígitos, com DDI 55 */
  whatsapp: z.string().regex(/^\d{10,15}$/).nullable().default(null),
  address: z.string().max(240).nullable().default(null),
  notes: z.string().max(2000).nullable().default(null),
  /** código de acesso do cliente ao link pessoal; gerado pelo servidor */
  access_code: z.string().nullable().default(null),
  last_visit_at: isoNullable.default(null),
});
export type Client = z.infer<typeof clientSchema>;

// ---------------------------------------------------------------- availability

export const availabilityRuleSchema = z.object({
  ...owned,
  /** 0 = domingo ... 6 = sábado */
  weekday: z.number().int().min(0).max(6),
  start: localTime,
  end: localTime,
});
export type AvailabilityRule = z.infer<typeof availabilityRuleSchema>;

export const WINDOW_KINDS = ['range', 'home_window'] as const;
export const availabilityWindowSchema = z.object({
  ...owned,
  date: localDate,
  start: localTime,
  end: localTime,
  kind: z.enum(WINDOW_KINDS).default('range'),
  region: z.string().max(120).nullable().default(null),
});
export type AvailabilityWindow = z.infer<typeof availabilityWindowSchema>;

export const blockSchema = z.object({
  ...owned,
  start_utc: isoDateTime,
  end_utc: isoDateTime,
  reason: z.string().max(120).nullable().default(null),
});
export type Block = z.infer<typeof blockSchema>;

// ---------------------------------------------------------------- appointment

export const APPOINTMENT_STATUSES = ['pending', 'scheduled', 'confirmed', 'completed', 'canceled', 'no_show'] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];
export const LOCATIONS = ['studio', 'home'] as const;
export type Location = (typeof LOCATIONS)[number];
export const SOURCES = ['professional', 'link'] as const;

export const appointmentSchema = z.object({
  ...owned,
  client_id: z.uuid(),
  service_id: z.uuid(),
  start_utc: isoDateTime,
  end_utc: isoDateTime,
  location: z.enum(LOCATIONS).default('studio'),
  address: z.string().max(240).nullable().default(null),
  status: z.enum(APPOINTMENT_STATUSES).default('scheduled'),
  source: z.enum(SOURCES).default('professional'),
  price_cents: z.number().int().min(0),
  discount_cents: z.number().int().min(0).default(0),
  notes: z.string().max(2000).nullable().default(null),
  /** marcado pelo servidor quando há choque de horário */
  conflict: z.boolean().default(false),
  /** a profissional decidiu manter os dois horários sobrepostos */
  overlap_ok: z.boolean().default(false),
  /** pacote usado ao concluir */
  package_id: z.uuid().nullable().default(null),
  completed_at: isoNullable.default(null),
  canceled_at: isoNullable.default(null),
  canceled_by: z.enum(['professional', 'client']).nullable().default(null),
});
export type Appointment = z.infer<typeof appointmentSchema>;

// ---------------------------------------------------------------- payment

export const PAYMENT_METHODS = ['pix', 'cash', 'card'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const paymentSchema = z.object({
  ...owned,
  appointment_id: z.uuid().nullable().default(null),
  package_id: z.uuid().nullable().default(null),
  /** venda de produtos do estoque */
  sale_id: z.uuid().nullable().default(null),
  client_id: z.uuid().nullable().default(null),
  amount_cents: z.number().int().min(0),
  method: z.enum(PAYMENT_METHODS),
  paid_at: isoDateTime,
});
export type Payment = z.infer<typeof paymentSchema>;

// ---------------------------------------------------------------- package

export const packageSchema = z.object({
  ...owned,
  client_id: z.uuid(),
  service_id: z.uuid(),
  sessions_total: z.number().int().min(1).max(500),
  /** sessões já feitas antes de cadastrar o pacote no app */
  sessions_used_initial: z.number().int().min(0).default(0),
  price_cents: z.number().int().min(0),
  purchased_at: isoDateTime,
  expires_at: localDate.nullable().default(null),
});
export type Package = z.infer<typeof packageSchema>;

// ---------------------------------------------------------------- message template

export const MESSAGE_KINDS = [
  'confirmation',
  'reminder',
  'delay',
  'reschedule',
  'missing',
  'open_agenda',
  'post_service',
] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

export const messageTemplateSchema = z.object({
  ...owned,
  kind: z.enum(MESSAGE_KINDS),
  text: z.string().trim().min(1).max(1000),
});
export type MessageTemplate = z.infer<typeof messageTemplateSchema>;

// ---------------------------------------------------------------- estoque

/**
 * Como ela conta o produto: por unidade (esmalte, lixa) ou pela medida da embalagem (removedor em ml,
 * gel em g). A quantidade em estoque é sempre na medida (un, ml ou g) e sai da soma dos movimentos.
 */
export const STOCK_UNITS = ['un', 'ml', 'g'] as const;
export type StockUnit = (typeof STOCK_UNITS)[number];

export const productSchema = z.object({
  ...owned,
  name: z.string().trim().min(1).max(80),
  unit: z.enum(STOCK_UNITS).default('un'),
  /** quanto vem numa embalagem, na medida (1 para "un"; 500 para um frasco de 500 ml) */
  pack_size: z.number().int().min(1).max(100_000).default(1),
  /** preço que ela pagou por embalagem na última entrada */
  cost_cents: z.number().int().min(0).max(10_000_000).default(0),
  /** preço de venda por embalagem; null = não vende, só usa nos atendimentos */
  price_cents: z.number().int().min(0).max(10_000_000).nullable().default(null),
  /** avisar quando ficar com menos que isso, na medida; null = não avisa */
  min_qty: z.number().int().min(0).max(10_000_000).nullable().default(null),
  active: z.boolean().default(true),
});
export type Product = z.infer<typeof productSchema>;

/** Entrada (compra), venda, gasto num atendimento ou correção da contagem. */
export const STOCK_MOVEMENT_KINDS = ['in', 'sale', 'use', 'adjust'] as const;
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];

export const stockMovementSchema = z.object({
  ...owned,
  product_id: z.uuid(),
  kind: z.enum(STOCK_MOVEMENT_KINDS),
  /** na medida do produto; positivo entra, negativo sai */
  qty: z.number().int().min(-10_000_000).max(10_000_000),
  /** total pago (entrada) */
  cost_cents: z.number().int().min(0).max(100_000_000).default(0),
  /** total cobrado (venda) */
  price_cents: z.number().int().min(0).max(100_000_000).default(0),
  appointment_id: z.uuid().nullable().default(null),
  sale_id: z.uuid().nullable().default(null),
  note: z.string().max(200).nullable().default(null),
  occurred_at: isoDateTime,
});
export type StockMovement = z.infer<typeof stockMovementSchema>;

/** Venda de produtos, junto com um atendimento ou avulsa. Os itens são os movimentos "sale". */
export const saleSchema = z.object({
  ...owned,
  client_id: z.uuid().nullable().default(null),
  appointment_id: z.uuid().nullable().default(null),
  total_cents: z.number().int().min(0).max(100_000_000),
  sold_at: isoDateTime,
});
export type Sale = z.infer<typeof saleSchema>;

/** Quanto um serviço gasta de um produto a cada atendimento (sai do estoque ao concluir). */
export const serviceSupplySchema = z.object({
  ...owned,
  service_id: z.uuid(),
  product_id: z.uuid(),
  /** na medida do produto */
  qty: z.number().int().min(1).max(100_000),
});
export type ServiceSupply = z.infer<typeof serviceSupplySchema>;

// ------------------------------------------------- serviços do atendimento

/**
 * Um serviço dentro de um atendimento. Um atendimento pode ter vários
 * ("pé e mão" + "esmaltação"), e a duração dele é a soma destes.
 *
 * Entidade filha, e não uma lista dentro do atendimento, por causa da
 * sincronização: ela resolve conflito campo a campo, por última escrita. Uma
 * lista seria um campo só — duas edições offline se atropelariam inteiras em
 * vez de mesclar, e a profissional perderia um serviço sem aviso.
 */
export const appointmentServiceSchema = z.object({
  ...owned,
  appointment_id: z.uuid(),
  service_id: z.uuid(),
  /** congelado na marcação: o serviço muda de preço, o atendimento passado não */
  price_cents: z.number().int().min(0).max(100_000_000),
  duration_min: z.number().int().min(0).max(24 * 60),
  /** a ordem em que ela escolheu */
  position: z.number().int().min(0).max(50).default(0),
});
export type AppointmentService = z.infer<typeof appointmentServiceSchema>;

// ---------------------------------------------------------------- registry

export const entitySchemas = {
  professional: professionalSchema,
  service: serviceSchema,
  client: clientSchema,
  availability_rule: availabilityRuleSchema,
  availability_window: availabilityWindowSchema,
  block: blockSchema,
  appointment: appointmentSchema,
  payment: paymentSchema,
  package: packageSchema,
  message_template: messageTemplateSchema,
  product: productSchema,
  stock_movement: stockMovementSchema,
  sale: saleSchema,
  service_supply: serviceSupplySchema,
  appointment_service: appointmentServiceSchema,
} as const;

export interface EntityMap {
  professional: Professional;
  service: Service;
  client: Client;
  availability_rule: AvailabilityRule;
  availability_window: AvailabilityWindow;
  block: Block;
  appointment: Appointment;
  payment: Payment;
  package: Package;
  message_template: MessageTemplate;
  product: Product;
  stock_movement: StockMovement;
  sale: Sale;
  service_supply: ServiceSupply;
  appointment_service: AppointmentService;
}

export type AnyEntity = EntityMap[EntityType];

/** Nome da tabela local / recurso da API para cada entidade. */
export const ENTITY_TABLES: Record<EntityType, string> = {
  professional: 'professionals',
  service: 'services',
  client: 'clients',
  availability_rule: 'availability_rules',
  availability_window: 'availability_windows',
  block: 'blocks',
  appointment: 'appointments',
  payment: 'payments',
  package: 'packages',
  message_template: 'message_templates',
  product: 'products',
  stock_movement: 'stock_movements',
  sale: 'sales',
  service_supply: 'service_supplies',
  appointment_service: 'appointment_services',
};

/** Campos que o aparelho nunca envia (o servidor controla). */
export const SERVER_MANAGED_FIELDS = ['created_at', 'updated_at', 'version', 'professional_id'] as const;
